/**
 * 工作区邀请页面
 * 用户通过邀请链接访问此页面，可以查看邀请信息并选择加入工作区
 */
import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { Button } from '@you/ui'
import { useWorkspaceInvite } from '@/hooks/useWorkspaceMemberActions'
import { useUserStore } from '@/stores/userStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { Workspace } from '@/api/types/workspace.types'
import { useWorkspaces } from '@/api/hooks/useWorkspace'

/**
 * 邀请信息展示组件
 */
const InviteInfo = ({
  inviters,
  invitersAvatar,
  workspaceName,
  workspaceIcon,
}: {
  inviters?: string
  invitersAvatar?: string
  workspaceName?: string
  workspaceIcon?: string
}) => {
  return (
    <div className="text-center space-y-6">
      {/* 工作区图标 */}
      <div className="flex justify-center">
        {workspaceIcon ? (
          <img
            src={workspaceIcon}
            alt={workspaceName}
            className="w-20 h-20 rounded-lg object-cover"
          />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
            {workspaceName?.charAt(0) || 'W'}
          </div>
        )}
      </div>

      {/* 邀请信息 */}
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">加入工作区</h1>

        {workspaceName && (
          <h2 className="text-xl font-semibold text-gray-700">
            {workspaceName}
          </h2>
        )}

        {inviters && (
          <div className="flex items-center justify-center space-x-3">
            {invitersAvatar ? (
              <img
                src={invitersAvatar}
                alt={inviters}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-medium">
                {inviters.charAt(0)}
              </div>
            )}
            <span className="text-gray-600">
              <span className="font-medium text-gray-900">{inviters}</span>{' '}
              邀请你加入
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * 加载状态组件
 */
const LoadingState = () => (
  <div className="text-center space-y-4">
    <div className="w-20 h-20 mx-auto rounded-lg bg-gray-200 animate-pulse"></div>
    <div className="space-y-2">
      <div className="h-6 bg-gray-200 rounded animate-pulse mx-auto w-48"></div>
      <div className="h-4 bg-gray-200 rounded animate-pulse mx-auto w-32"></div>
    </div>
  </div>
)

/**
 * 错误状态组件
 */
const ErrorState = ({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}) => (
  <div className="text-center space-y-4">
    <div className="w-20 h-20 mx-auto rounded-lg bg-red-100 flex items-center justify-center">
      <span className="text-red-500 text-3xl">⚠️</span>
    </div>
    <div className="space-y-2">
      <h1 className="text-xl font-bold text-gray-900">邀请无效</h1>
      <p className="text-gray-600">{message}</p>
      <Button onClick={onRetry} variant="outline">
        重试
      </Button>
    </div>
  </div>
)

/**
 * 成功状态组件
 */
const SuccessState = ({
  workspaceName,
  onContinue,
}: {
  workspaceName?: string
  onContinue: () => void
}) => (
  <div className="text-center space-y-4">
    <div className="w-20 h-20 mx-auto rounded-lg bg-green-100 flex items-center justify-center">
      <span className="text-green-500 text-3xl">✅</span>
    </div>
    <div className="space-y-2">
      <h1 className="text-xl font-bold text-gray-900">加入成功！</h1>
      <p className="text-gray-600">
        你已成功加入工作区 {workspaceName && `"${workspaceName}"`}
      </p>
      <Button onClick={onContinue}>进入工作区</Button>
    </div>
  </div>
)

/**
 * 工作区邀请页面主组件
 */
const WorkspaceInvite = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser } = useUserStore()
  const { setCurrentWorkspace } = useWorkspaceStore()
  const { data: workspaces, mutate: refreshWorkspaces } = useWorkspaces()
  const [targetWorkspace, setTargetWorkspace] = useState<Workspace | null>(null)

  // 从URL获取邀请码
  const code = searchParams.get('code')

  // 使用邀请Hook
  const {
    inviteData,
    isAccepted,
    isLoadingInvite,
    isAccepting,
    inviteError,
    acceptError,
    handleAcceptInvite,
    refetchInvite,
  } = useWorkspaceInvite(code || undefined)

  // 判断是否已加入该空间
  const isAlreadyJoined =
    acceptError instanceof Error && acceptError.message === '您已加入该工作空间'

  // 构建完整的邀请URL（避免重复编码问题）
  const buildInviteUrl = () => {
    const currentHost = window.location.origin
    return `${currentHost}/workspace/invite?code=${code}`
  }

  // 检查是否需要登录或登录信息过期
  useEffect(() => {
    // 只在邀请页面路径下进行检查，避免在登录页面被误触发
    if (
      location.pathname.includes('/workspace/invite') &&
      !currentUser &&
      code
    ) {
      // 构建正确的邀请URL作为重定向参数
      const inviteUrl = buildInviteUrl()
      const redirectUrl = encodeURIComponent(inviteUrl)

      console.log('用户未登录，跳转到登录页面，重定向URL:', inviteUrl)

      // 跳转到登录页面，携带重定向参数
      navigate(`/login?redirect=${redirectUrl}`)
    }
  }, [currentUser, navigate, location.pathname, code])

  // 监听接口错误，如果是登录过期相关错误，跳转到登录页
  useEffect(() => {
    // 只在邀请页面路径下进行错误检查
    if (!location.pathname.includes('/workspace/invite') || !code) {
      return
    }

    if (inviteError || acceptError) {
      const error = inviteError || acceptError
      // 检查是否是登录相关的错误（401, 403 或特定错误消息）
      const isAuthError =
        error?.message?.includes('未登录') ||
        error?.message?.includes('登录过期') ||
        error?.message?.includes('认证失败') ||
        error?.message?.includes('无效token') ||
        error?.message?.includes('token过期') ||
        // 检查HTTP状态码
        (error as any)?.status === 401 ||
        (error as any)?.status === 403

      if (isAuthError) {
        console.log('检测到登录过期错误，跳转到登录页面:', error)

        // 构建正确的邀请URL作为重定向参数
        const inviteUrl = buildInviteUrl()
        const redirectUrl = encodeURIComponent(inviteUrl)

        // 清除用户信息并跳转到登录页
        const { reset } = useUserStore.getState()
        reset()

        navigate(`/login?redirect=${redirectUrl}`)
      }
    }
  }, [inviteError, acceptError, navigate, location.pathname, code])

  // 查找目标工作区
  const findTargetWorkspace = useCallback(() => {
    if (inviteData?.workspaceId && workspaces?.workspaces) {
      const workspace = workspaces.workspaces.find(
        (w: Workspace) => w.id === inviteData.workspaceId
      )
      setTargetWorkspace(workspace || null)
      return workspace
    }
    return null
  }, [inviteData?.workspaceId, workspaces?.workspaces])

  // 监听工作区列表变化，尝试找到目标工作区
  useEffect(() => {
    findTargetWorkspace()
  }, [findTargetWorkspace])

  // 处理继续操作（进入工作区）
  const handleContinue = () => {
    if (targetWorkspace) {
      setCurrentWorkspace(targetWorkspace)
      navigate('/workspace')
    }
  }

  // 处理加入或进入操作
  const handleJoinOrEnter = async () => {
    if (isAlreadyJoined || targetWorkspace) {
      handleContinue()
    } else {
      try {
        await handleAcceptInvite()
        // 刷新工作区列表
        await refreshWorkspaces()
      } catch (error) {
        console.error('加入工作区失败:', error)
      }
    }
  }

  // 处理重试操作
  const handleRetry = () => {
    refetchInvite()
  }

  // 如果用户未登录，不渲染任何内容（将重定向到登录页）
  if (!currentUser) {
    return null
  }

  // 如果没有邀请码，显示错误
  if (!code) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
          <ErrorState
            message="缺少邀请码，请检查邀请链接是否正确"
            onRetry={() => window.location.reload()}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        {/* 加载状态 */}
        {isLoadingInvite && <LoadingState />}

        {/* 错误状态 */}
        {!isLoadingInvite && inviteError && (
          <ErrorState
            message={inviteError?.message || '获取邀请信息失败'}
            onRetry={handleRetry}
          />
        )}

        {/* 成功状态 */}
        {!isLoadingInvite && isAccepted && targetWorkspace && (
          <SuccessState
            workspaceName={inviteData?.workspaceName}
            onContinue={handleContinue}
          />
        )}

        {/* 邀请信息内容 */}
        {!isLoadingInvite && !inviteError && !isAccepted && inviteData && (
          <div className="space-y-6">
            {/* 邀请信息展示 */}
            <InviteInfo
              inviters={inviteData.inviters}
              invitersAvatar={inviteData.invitersAvatar}
              workspaceName={inviteData.workspaceName}
              workspaceIcon={inviteData.workspaceIcon}
            />

            {/* 已加入空间的提示 */}
            {(isAlreadyJoined || targetWorkspace) && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-yellow-500 text-lg">⚠️</span>
                  <div>
                    <p className="text-yellow-800 font-medium">
                      您已加入该工作空间
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 接受邀请失败的错误提示（非已加入空间的情况） */}
            {acceptError && !isAlreadyJoined && !targetWorkspace && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-red-500 text-lg">⚠️</span>
                  <div>
                    <p className="text-red-800 font-medium">
                      加入空间失败，请联系邀请人
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 按钮组 */}
            <div className="space-y-3">
              <Button
                onClick={handleJoinOrEnter}
                disabled={isAccepting}
                className="w-full"
              >
                {isAccepting
                  ? '加入中...'
                  : isAlreadyJoined || targetWorkspace
                  ? '立即进入'
                  : '接受邀请'}
              </Button>

              <Button
                onClick={() => navigate('/workspace')}
                variant="outline"
                className="w-full"
              >
                取消
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkspaceInvite
